import React from 'react';
import { Box, Container, Typography, Link, Divider, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const LAST_UPDATED = 'June 3, 2026';

const SECTIONS = [
  {
    heading: '1. Acceptance of Terms',
    body: [
      'BookMyLibrary is operated by J2 Learning Pvt. Ltd. ("we", "us", "our"). By creating an account, booking a seat, or otherwise using our platform, you agree to be bound by these Terms and Conditions and our Privacy Policy. If you do not agree, please do not use the platform.',
    ],
  },
  {
    heading: '2. Eligibility and Accounts',
    body: [
      'You must be able to lawfully enter into a binding agreement to use our services. You are responsible for keeping your account credentials secure and for all activity that takes place under your account.',
      'You agree to provide accurate, current information — including a valid phone number — and to keep it up to date. We may suspend or terminate accounts that contain false information or that are used in breach of these terms.',
    ],
  },
  {
    heading: '3. Bookings and Check-in',
    body: [
      'Seat bookings are subject to availability and the plan you select (daily, weekly, monthly, or half-yearly). A booking is confirmed only once payment has been completed and, where applicable, accepted by the library.',
      'Check-in and check-out are performed by scanning your QR pass. Where a plan limits the number of study hours per day, access is governed by that limit. Misuse of QR passes, sharing access, or attempting to circumvent time limits may result in cancellation without refund.',
    ],
  },
  {
    heading: '4. Wallet, Payments, and Pricing',
    body: [
      'Payments for wallet top-ups and subscriptions are processed securely by Razorpay. We do not store your card or bank details.',
      'Wallet balances may be used to pay for bookings and subscriptions. Prices for plans are set by individual libraries and are shown before you confirm a purchase. Applicable taxes, if any, are charged in addition.',
    ],
  },
  {
    heading: '5. Cancellations and Refunds',
    body: [
      'Cancellation and refund eligibility depends on the plan and the policy of the library you booked with, as shown at the time of booking. Where a refund is due, it is credited to your wallet or original payment method at our discretion.',
      'We do not offer refunds for access already used, for cancellations made in breach of these terms, or where a booking is terminated due to misuse.',
    ],
  },
  {
    heading: '6. BML Coins and Rewards',
    body: [
      'BML Coins and similar rewards are promotional features with no cash value. They cannot be exchanged for money and may be modified, expired, or withdrawn at any time. We may adjust balances to correct errors or to address abuse.',
    ],
  },
  {
    heading: '7. Acceptable Use',
    body: [
      'You agree not to misuse the platform, including by attempting to gain unauthorised access, disrupting service, uploading unlawful or harmful content, harassing other users or staff, or using the platform for any purpose other than its intended use.',
      'Librarians, institute managers, and other staff must use the data they can access solely to operate their library or institute and in accordance with applicable law and our Privacy Policy.',
    ],
  },
  {
    heading: '8. Libraries and Institutes',
    body: [
      'Libraries and coaching institutes listed on the platform are independently operated. We facilitate discovery, booking, and payments, but we are not responsible for the conduct of any library or institute, the condition of their premises, or the quality of services they provide.',
    ],
  },
  {
    heading: '9. Intellectual Property',
    body: [
      'The BookMyLibrary platform, including its software, design, and content (excluding content you or third parties provide), is owned by us or our licensors and is protected by applicable intellectual property laws. You may not copy, modify, or distribute it without our permission.',
    ],
  },
  {
    heading: '10. Disclaimers and Limitation of Liability',
    body: [
      'The platform is provided on an "as is" and "as available" basis without warranties of any kind. We do not guarantee uninterrupted or error-free service.',
      'To the maximum extent permitted by law, we are not liable for any indirect, incidental, or consequential losses arising from your use of the platform. Our total liability for any claim is limited to the amount you paid to us in connection with the relevant transaction.',
    ],
  },
  {
    heading: '11. Termination',
    body: [
      'We may suspend or terminate your access to the platform at any time if you breach these terms or if we are required to do so by law. You may stop using the platform at any time and request deletion of your account, subject to records we are required to retain.',
    ],
  },
  {
    heading: '12. Governing Law',
    body: [
      'These Terms and Conditions are governed by the laws of India. Any disputes are subject to the exclusive jurisdiction of the competent courts in India.',
    ],
  },
  {
    heading: '13. Changes to These Terms',
    body: [
      'We may update these Terms and Conditions from time to time. Material changes will be reflected by updating the "Last updated" date at the top of this page. Your continued use of the platform after changes take effect constitutes acceptance of the revised terms.',
    ],
  },
];

/**
 * TermsPage — public terms and conditions.
 * Renders inside AppShell (Aurora background + Footer).
 */
function TermsPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const muted = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)';

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
        Terms &amp; Conditions
      </Typography>
      <Typography variant="body2" sx={{ color: muted, mb: 4 }}>
        Last updated: {LAST_UPDATED}
      </Typography>

      <Typography variant="body1" sx={{ color: muted, lineHeight: 1.8, mb: 4 }}>
        These Terms and Conditions govern your use of BookMyLibrary, operated by J2 Learning Pvt.
        Ltd., a platform to discover, book, and manage seats at study libraries and coaching
        institutes across India. Please read them carefully before using our services.
      </Typography>

      {SECTIONS.map((section) => (
        <Box key={section.heading} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            {section.heading}
          </Typography>
          {section.body.map((paragraph, i) => (
            <Typography
              key={i}
              variant="body1"
              sx={{ color: muted, lineHeight: 1.8, mb: 1.5 }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>
      ))}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          14. Contact Us
        </Typography>
        <Typography variant="body1" sx={{ color: muted, lineHeight: 1.8 }}>
          For any questions about these Terms and Conditions, email us at{' '}
          <Link href="mailto:support@bookmylibrary.in" underline="hover" sx={{ fontWeight: 600 }}>
            support@bookmylibrary.in
          </Link>
          .
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Link component={RouterLink} to="/" underline="hover" sx={{ fontWeight: 600 }}>
        ← Back to home
      </Link>
    </Container>
  );
}

export default TermsPage;
